import React, { useState, useEffect, useRef } from 'react';
import { Card, Radio, Input, Button, Space, Typography, message, Alert, Spin, Result, Modal } from 'antd';
import { getContents, submitPromise, getProgress } from '../../api/learn';
import { CheckCircleOutlined, LockOutlined, DownloadOutlined, PictureOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import { useAuth } from '../../utils/auth';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const Step6Promise = ({ onComplete }) => {
  const { user } = useAuth();
  const [contents, setContents] = useState([]);
  const [weakItem, setWeakItem] = useState(null);
  const [promiseContent, setPromiseContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingPromise, setExistingPromise] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [posterLoading, setPosterLoading] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const posterRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // 加载准则内容
      const contentsRes = await getContents();
      if (contentsRes.code === 200) {
        setContents(contentsRes.data.contents);
      }

      // 检查是否已提交
      const progressRes = await getProgress();
      if (progressRes.code === 200) {
        if (progressRes.data.step6_completed) {
          setSubmitted(true);
          // 如果有保存的承诺内容，显示出来
          if (progressRes.data.step6_weak_item && progressRes.data.step6_promise) {
            setExistingPromise({
              weak_item: progressRes.data.step6_weak_item,
              promise_content: progressRes.data.step6_promise
            });
          }
        }
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setPageLoading(false);
    }
  };

  const handleWeakItemChange = (e) => {
    setWeakItem(e.target.value);
  };

  const handlePromiseChange = (e) => {
    setPromiseContent(e.target.value);
  };

  const handleSubmit = async () => {
    if (!weakItem) {
      message.warning('请选择您最薄弱的一项');
      return;
    }

    if (promiseContent.length < 50) {
      message.warning('承诺内容至少50字');
      return;
    }

    try {
      setLoading(true);
      
      const response = await submitPromise(weakItem, promiseContent);
      
      if (response.code === 200) {
        message.success('承诺提交成功！');
        setSubmitted(true);
        setExistingPromise({
          weak_item: weakItem,
          promise_content: promiseContent
        });
        // 不自动跳转，让用户查看结果和生成海报
      }
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  // 下载海报
  const downloadPoster = async () => {
    if (posterRef.current) {
      try {
        setPosterLoading(true);
        const canvas = await html2canvas(posterRef.current, {
          scale: 2,
          backgroundColor: null,
          useCORS: true,
          logging: false
        });
        
        const link = document.createElement('a');
        link.download = `更好的自己-承诺海报-${new Date().toLocaleDateString()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        message.success('海报已保存！');
      } catch (error) {
        console.error('生成海报失败:', error);
        message.error('生成海报失败，请重试');
      } finally {
        setPosterLoading(false);
      }
    }
  };

  // 页面加载中
  if (pageLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 获取当前承诺数据
  const currentWeakItem = existingPromise?.weak_item || weakItem;
  const currentPromiseContent = existingPromise?.promise_content || promiseContent;
  const weakItemContent = contents.find(c => c.item_number === currentWeakItem);

  // 已提交 - 显示承诺内容和海报生成
  if (submitted) {
    return (
      <div className="step-promise">
        <div className="step-header">
          <Title level={3}>步骤六：自查与承诺</Title>
          <Text type="secondary">您的承诺已提交</Text>
        </div>

        <Card style={{ marginBottom: 24 }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="承诺提交成功！"
            subTitle="感谢您的认真对待，让我们一起践行承诺！"
            status="success"
            extra={[
              <Button 
                type="primary" 
                icon={<PictureOutlined />}
                onClick={() => setShowPoster(true)}
                key="poster"
                size="large"
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                生成承诺海报
              </Button>,
              <Button 
                key="complete"
                size="large"
                onClick={onComplete}
              >
                完成本步骤
              </Button>
            ]}
          />
        </Card>

        <Card 
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <span>我的承诺内容</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div style={{ 
              background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
              padding: 20,
              borderRadius: 8
            }}>
              <Text strong style={{ fontSize: 14, color: '#2e7d32' }}>
                选择改进的准则：
              </Text>
              <br />
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1b5e20' }}>
                {weakItemContent?.item_number}. {weakItemContent?.title}
              </Text>
              {weakItemContent && (
                <Paragraph style={{ marginTop: 8, marginBottom: 0, color: '#388e3c' }}>
                  {weakItemContent.content}
                </Paragraph>
              )}
            </div>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              padding: 20,
              borderRadius: 8
            }}>
              <Text strong style={{ fontSize: 14, color: '#e65100' }}>
                我的改进承诺：
              </Text>
              <Paragraph style={{ 
                fontSize: 16, 
                lineHeight: 1.8,
                color: '#bf360c',
                marginTop: 12,
                marginBottom: 0
              }}>
                {currentPromiseContent}
              </Paragraph>
            </div>

            <Alert
              message="温馨提示"
              description="承诺内容已保存，建议生成海报保存留念，时刻提醒自己践行承诺！"
              type="info"
              showIcon
            />
          </Space>
        </Card>

        {/* 海报预览弹窗 */}
        <Modal
          title={null}
          open={showPoster}
          onCancel={() => setShowPoster(false)}
          footer={[
            <Button key="close" onClick={() => setShowPoster(false)}>
              关闭
            </Button>,
            <Button 
              key="download" 
              type="primary" 
              icon={<DownloadOutlined />}
              loading={posterLoading}
              onClick={downloadPoster}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              下载海报
            </Button>
          ]}
          width={480}
          centered
        >
          {/* 积极正向的海报设计 */}
          <div 
            ref={posterRef}
            style={{
              background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 50%, #81c784 100%)',
              padding: '48px 32px',
              borderRadius: 16,
              color: '#fff',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Microsoft YaHei", sans-serif',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }}
          >
            {/* 顶部装饰 */}
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ 
                fontSize: 56, 
                marginBottom: 12,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}>
                🌟
              </div>
              <Title level={1} style={{ 
                color: '#fff', 
                margin: 0, 
                fontWeight: 'bold',
                textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                fontSize: 32
              }}>
                更好的自己
              </Title>
              <div style={{ 
                width: 80, 
                height: 4, 
                background: 'rgba(255,255,255,0.9)', 
                margin: '16px auto',
                borderRadius: 2
              }} />
              <div style={{ fontSize: 15, opacity: 0.95, marginTop: 8 }}>
                我承诺，我践行，我成长
              </div>
            </div>

            {/* 选择改进的准则 */}
            <div style={{ 
              background: 'rgba(255,255,255,0.25)', 
              borderRadius: 12, 
              padding: '20px 24px',
              marginBottom: 16,
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ 
                fontSize: 13, 
                opacity: 0.9, 
                marginBottom: 10,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                📌 选择改进的准则
              </div>
              <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 8 }}>
                {weakItemContent?.item_number}. {weakItemContent?.title}
              </div>
              {weakItemContent && (
                <div style={{ fontSize: 14, opacity: 0.9, lineHeight: 1.5 }}>
                  {weakItemContent.content}
                </div>
              )}
            </div>

            {/* 我的承诺 */}
            <div style={{ 
              background: 'rgba(255,255,255,0.25)', 
              borderRadius: 12, 
              padding: '20px 24px',
              marginBottom: 24,
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ 
                fontSize: 13, 
                opacity: 0.9, 
                marginBottom: 10,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                💪 我的改进承诺
              </div>
              <div style={{ 
                fontSize: 16, 
                lineHeight: 1.7,
                fontWeight: 500
              }}>
                {currentPromiseContent}
              </div>
            </div>

            {/* 底部 */}
            <div style={{ 
              textAlign: 'center', 
              paddingTop: 20,
              borderTop: '1px solid rgba(255,255,255,0.3)'
            }}>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 'bold',
                marginBottom: 8,
                textShadow: '0 1px 4px rgba(0,0,0,0.1)'
              }}>
                以行践言，以己之变，促企之兴
              </div>
              <div style={{ 
                fontSize: 14, 
                opacity: 0.95, 
                marginBottom: 8,
                fontWeight: 500 
              }}>
                {user?.name || ''}（工号：{user?.employee_id || ''}）
              </div>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>
                华工图像 · 六项行为准则学习
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // 未提交 - 可编辑
  return (
    <div className="step-promise">
      <div className="step-header">
        <Title level={3}>步骤六：自查与承诺</Title>
        <Text type="secondary">请对照六项行为准则，选择您认为自己最薄弱的一项，并做出整改承诺</Text>
      </div>

      <Card className="weak-item-card" title="请选择最薄弱项" style={{ marginBottom: 24 }}>
        <Radio.Group onChange={handleWeakItemChange} value={weakItem}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {contents.map(item => (
              <Radio 
                key={item.item_number} 
                value={item.item_number}
                style={{ fontSize: 15 }}
              >
                <Space direction="vertical" size="small">
                  <Text strong>
                    {item.item_number}. {item.title}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {item.content}
                  </Text>
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Card>

      {weakItem && (
        <Card className="promise-card" title="请填写承诺内容">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="承诺要求"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>说明为什么觉得这项薄弱</li>
                  <li>具体的改进计划</li>
                  <li>1个月内如何整改</li>
                </ul>
              }
              type="info"
              showIcon
            />

            <TextArea
              rows={8}
              value={promiseContent}
              onChange={handlePromiseChange}
              placeholder={`我选择"${contents.find(c => c.item_number === weakItem)?.title}"作为最薄弱项，因为在过去的工作中...`}
              maxLength={500}
              showCount
            />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">
                已输入：{promiseContent.length} 字
                {promiseContent.length > 0 && promiseContent.length < 50 && (
                  <Text type="danger" style={{ marginLeft: 8 }}>
                    (还需{50 - promiseContent.length}字)
                  </Text>
                )}
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading}
              disabled={promiseContent.length < 50}
              block
            >
              提交承诺
            </Button>

            <Alert
              message="重要提示"
              description="提交后将无法修改，请认真填写您的承诺内容！"
              type="warning"
              showIcon
            />
          </Space>
        </Card>
      )}
    </div>
  );
};

export default Step6Promise;
